import type { ReactNode } from 'react';
import { AlertTriangle, CheckCircle, Info, AlertCircle, X } from 'lucide-react';
import { cn } from '../lib/cn';

type Tone = 'info' | 'success' | 'warning' | 'error';

const TONES: Record<Tone, { box: string; icon: ReactNode }> = {
  info: {
    box: 'bg-sky-50 border-sky-200 text-sky-900 dark:bg-sky-950/30 dark:border-sky-900 dark:text-sky-200',
    icon: <Info className="w-4 h-4 text-sky-600 dark:text-sky-400" />,
  },
  success: {
    box: 'bg-emerald-50 border-emerald-200 text-emerald-900 dark:bg-emerald-950/30 dark:border-emerald-900 dark:text-emerald-200',
    icon: <CheckCircle className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />,
  },
  warning: {
    box: 'bg-amber-50 border-amber-200 text-amber-900 dark:bg-amber-950/30 dark:border-amber-900 dark:text-amber-200',
    icon: <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400" />,
  },
  error: {
    box: 'bg-rose-50 border-rose-200 text-rose-900 dark:bg-rose-950/30 dark:border-rose-900 dark:text-rose-200',
    icon: <AlertCircle className="w-4 h-4 text-rose-600 dark:text-rose-400" />,
  },
};

/**
 * Inline banner — used at the top of pages for system/policy messages
 * (suspended subscription, terms re-accept, dunning warning).
 */
export function Banner({
  tone = 'info',
  title,
  children,
  action,
  onDismiss,
  className,
}: {
  tone?: Tone;
  title?: ReactNode;
  children: ReactNode;
  action?: ReactNode;
  onDismiss?: () => void;
  className?: string;
}) {
  const { box, icon } = TONES[tone];
  return (
    <div
      role="status"
      className={cn(
        'flex items-start gap-3 px-4 sm:px-5 py-3 rounded-lg border text-sm',
        box,
        className,
      )}
    >
      <span className="flex-shrink-0 mt-0.5">{icon}</span>
      <div className="flex-1 min-w-0">
        {title && <div className="font-medium">{title}</div>}
        <div className={title ? 'mt-0.5 opacity-90' : ''}>{children}</div>
      </div>
      {action && <div className="flex-shrink-0">{action}</div>}
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="flex-shrink-0 w-6 h-6 -mr-1 -mt-1 grid place-items-center rounded text-current opacity-60 hover:opacity-100 hover:bg-current/10 transition"
          aria-label="Dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}
